import { useEffect, useMemo, useRef, useState } from 'react';
import { getFontByLocation, getFontNameFromBrandKitPure } from '@goldcast/player-config/src/utils/fontUtils';
import { SpeakerWithDetails } from '@goldcast/player-config/src/types/speaker-types';
import { ClipMetadata } from '@goldcast/player-config/src/types/clip-types';
import Square from '../MagicLayout/Audiogram/Square';
import Portrait from '../MagicLayout/Audiogram/Portrait';
import Landscape from '../MagicLayout/Audiogram/Landscape';
import { AudiogramCommonProps } from '../MagicLayout/Audiogram/types';
import { getTruncatedName, getTruncatedTitle } from '../CompositePlayer/SpeakerVideoUtils';
import { PreviewTemplate } from './types';
import { brandKit } from '@/stores/brandKit';

export default function AudiogramPreview({
  clipTemplate,
  currentSpeaker
}: {
  clipTemplate: PreviewTemplate | ClipMetadata;
  currentSpeaker: SpeakerWithDetails;
}) {
  const [truncatedTitle, setTruncatedTitle] = useState('');
  const [truncatedName, setTruncatedName] = useState('');

  const size = clipTemplate.size;
  const templatePreviewContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentSpeaker) {
      setTruncatedTitle(getTruncatedTitle(currentSpeaker, 'AUDIOGRAM', size));
      setTruncatedName(getTruncatedName(`${currentSpeaker.first_name} ${currentSpeaker.last_name}`, 'AUDIOGRAM', size));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpeaker]);

  const fontName = useMemo(() => {
    const fontItem = getFontByLocation(clipTemplate.font_location);
    if (fontItem) {
      return fontItem.name;
    }
    return getFontNameFromBrandKitPure({ fontUrl: clipTemplate.font_location, brandKitStore: brandKit.getSnapshot() });
  }, [clipTemplate.font_location]);

  const commonProps: AudiogramCommonProps = {
    backgroundColor: clipTemplate.magicLayout?.backgroundColor!,
    backgroundImage: clipTemplate.magicLayout?.backgroundImage,
    textColor: clipTemplate.magicLayout?.textColor!,
    speakerLabelStyle: clipTemplate.speaker_label_styles
      ? {
          fontFamily: getFontByLocation(clipTemplate.speaker_label_styles?.font_location)?.name || '',
          lineHeight: '1.2',
          color: clipTemplate.speaker_label_styles?.text_color
        }
      : undefined,
    width: templatePreviewContainer.current?.getBoundingClientRect().width || 0,
    height: templatePreviewContainer.current?.getBoundingClientRect().height || 0,
    activeSpeaker: currentSpeaker,
    truncatedTitle,
    truncatedName,
    fontFamilyStyle: {
      fontFamily: fontName || '',
      lineHeight: '1.2'
    },
    isTemplatePreview: true
  };

  return (
    <>
      <div
        className="absolute left-0 top-0 size-full rounded bg-white"
        data-testid="audiogram-container"
        ref={templatePreviewContainer}
      />

      {size === 'SQUARE' && <Square sizeMultiplier={0.5} {...commonProps} />}
      {size === 'PORTRAIT' && <Portrait sizeMultiplier={0.9} {...commonProps} />}
      {size === 'LANDSCAPE' && <Landscape sizeMultiplier={0.8} {...commonProps} />}
    </>
  );
}
